import { ReactNode } from "react";

export type Props = {
  /** Primary button label */
  buttonLabel: string;
  /** Primary button click handler */
  buttonOnClick: () => void;
  /** Dropdown options to render */
  dropdownOptions: ReactNode[];
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** aria-pressed state for the primary button (for toggle buttons) */
  buttonAriaPressed?: boolean;
  /** Render the primary button */
  renderButton: (props: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    ariaPressed?: boolean;
  }) => ReactNode;
  /** Render the dropdown trigger button */
  renderDropdownTrigger: (props: { disabled?: boolean }) => ReactNode;
  /** Render the dropdown container */
  renderDropdown: (props: {
    trigger: ReactNode;
    children: ReactNode;
    disabled?: boolean;
  }) => ReactNode;
  /** Test ID for testing */
  "data-testid"?: string;
};

/**
 * Base button with dropdown combo component with no styling.
 * Combines a primary action button with a dropdown for additional options.
 * Use render props to customize the buttons and dropdown.
 *
 * @example
 * // In shared/ui/ButtonDropdownCombo.tsx
 * import { ButtonDropdownComboBase, type Props as ButtonDropdownComboBaseProps } from "@core/ui";
 *
 * export function ButtonDropdownCombo(props: ButtonDropdownComboBaseProps) {
 *   return (
 *     <ButtonDropdownComboBase
 *       {...props}
 *       renderButton={({ label, onClick }) => (
 *         <Button onClick={onClick}>{label}</Button>
 *       )}
 *       renderDropdownTrigger={() => (
 *         <Button><ChevronDown /></Button>
 *       )}
 *       renderDropdown={({ trigger, children }) => (
 *         <Dropdown trigger={trigger}>{children}</Dropdown>
 *       )}
 *     />
 *   );
 * }
 */
export function ButtonDropdownComboBase({
  buttonLabel,
  buttonOnClick,
  dropdownOptions,
  disabled = false,
  buttonAriaPressed,
  className,
  renderButton,
  renderDropdownTrigger,
  renderDropdown,
  "data-testid": testId = "button-dropdown-combo",
}: Props) {
  const hasOptions = dropdownOptions && dropdownOptions.length > 0;

  return (
    <div className={className} data-testid={testId}>
      {renderButton({
        label: buttonLabel,
        onClick: buttonOnClick,
        disabled,
        ariaPressed: buttonAriaPressed,
      })}

      {hasOptions && !disabled && (
        renderDropdown({
          trigger: renderDropdownTrigger({ disabled }),
          children: (
            <div data-testid={`${testId}-options`}>
              {dropdownOptions.map((option, index) => (
                <span key={index}>{option}</span>
              ))}
            </div>
          ),
          disabled,
        })
      )}
    </div>
  );
}

ButtonDropdownComboBase.displayName = "ButtonDropdownComboBase";

